import { Link } from "@tanstack/react-router";
import { graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { booksPage_Query } from "@app/queries/booksPage_Query.graphql";

export const booksQuery = graphql`
  query booksPage_Query($from: String) {
    books(from: $from) {
      trialBalance {
        code
        name
        kind
        balance
      }
      balanceSheet {
        asOf
        balances
        totalAssets
        totalLiabilities
        totalEquity
        assets {
          code
          name
          balance
        }
        liabilities {
          code
          name
          balance
        }
        equity {
          code
          name
          balance
        }
      }
      incomeStatement {
        revenue
        totalExpense
        netIncome
        expenses {
          line
          amount
        }
      }
      health {
        balanced
        equationHolds
        suspense
        transferClearing
        walletClearing
      }
    }
  }
`;

function money(value: number): string {
  const abs = Math.abs(value);
  const text =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(2)}M`
      : abs >= 1_000
      ? `${(abs / 1_000).toFixed(1)}k`
      : abs.toFixed(0);
  return `${value < 0 ? "-" : ""}$${text}`;
}

type Balance = { readonly code: string; readonly name: string; readonly balance: number };

export default function BooksPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<booksPage_Query>;
}) {
  const { books } = usePreloadedQuery(booksQuery, queryRef);

  if (books.trialBalance.length === 0) {
    return (
      <div className="p-6 text-gray-500">
        No books yet — import statements first.
      </div>
    );
  }

  const { health, balanceSheet: bs, incomeStatement: is } = books;
  const healthy =
    health.balanced &&
    health.equationHolds &&
    Math.abs(health.suspense) < 1 &&
    bs.balances;

  return (
    <div className="p-6 max-w-full mx-auto space-y-6">
      <h1 className="text-xl font-bold">Books (general ledger)</h1>

      <HealthBanner health={health} balances={bs.balances} healthy={healthy} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={`Income statement`}>
          <StatementTable
            rows={[
              { label: "Revenue", value: is.revenue, strong: true },
              ...is.expenses.map((e) => ({
                label: e.line,
                value: -e.amount,
                indent: true,
              })),
              { label: "Total expense", value: -is.totalExpense },
              { label: "Net income", value: is.netIncome, strong: true },
            ]}
          />
        </Section>

        <Section title={`Balance sheet — as of ${bs.asOf}`}>
          <StatementTable
            rows={[
              { label: "ASSETS", value: bs.totalAssets, strong: true },
              ...bs.assets.map((a) => sub(a)),
              { label: "LIABILITIES", value: bs.totalLiabilities, strong: true },
              ...bs.liabilities.map((a) => sub(a)),
              { label: "EQUITY", value: bs.totalEquity, strong: true },
              ...bs.equity.map((a) => sub(a)),
            ]}
          />
        </Section>
      </div>

      <Section title="Trial balance">
        <table className="text-xs w-full">
          <thead>
            <tr className="border-b text-gray-500">
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-right px-3 py-2">Balance</th>
            </tr>
          </thead>
          <tbody>
            {books.trialBalance.map((row) => (
              <tr key={row.code} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-3 py-1.5">
                  <Link
                    to="/books/$code"
                    params={{ code: row.code }}
                    className="text-blue-600 hover:underline"
                  >
                    {row.name}
                  </Link>
                  <span className="text-gray-400 ml-2">{row.code}</span>
                </td>
                <td className="px-3 py-1.5 text-gray-500">{row.kind}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {money(row.balance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <p className="text-xs text-gray-400">
        The books are derived from your transactions each rebuild. A non-zero
        suspense or a failed invariant means something is miscoded — that signal
        is the whole point. Cash ties to your statements; the income statement
        matches the P&L page.
      </p>
    </div>
  );
}

function sub(b: Balance) {
  return { label: `${b.name}`, value: b.balance, indent: true };
}

function HealthBanner({
  health,
  balances,
  healthy,
}: {
  health: booksPage_Query["response"]["books"]["health"];
  balances: boolean;
  healthy: boolean;
}) {
  const cards = [
    { label: "Books balanced", ok: health.balanced },
    { label: "Equation holds", ok: health.equationHolds },
    { label: "Sheet balances", ok: balances },
  ];
  const plugs = [
    { label: "Suspense", value: health.suspense },
    { label: "Transfer clearing", value: health.transferClearing },
    { label: "Wallet clearing", value: health.walletClearing },
  ];

  return (
    <div
      className={`rounded border p-4 ${
        healthy ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
      }`}
    >
      <p className="font-semibold mb-2">
        {healthy ? "Books are healthy" : "Books need attention"}
      </p>
      <div className="flex flex-wrap gap-3 text-sm">
        {cards.map((c) => (
          <span key={c.label} className={c.ok ? "text-green-700" : "text-red-700"}>
            {c.ok ? "✓" : "✗"} {c.label}
          </span>
        ))}
        {plugs.map((p) => (
          <span key={p.label} className="text-gray-600">
            {p.label}: <span className="tabular-nums">{money(p.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h2 className="font-bold">{title}</h2>
      <div className="overflow-x-auto bg-white rounded border">{children}</div>
    </div>
  );
}

function StatementTable({
  rows,
}: {
  rows: { label: string; value: number; strong?: boolean; indent?: boolean }[];
}) {
  return (
    <table className="text-xs w-full">
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={`${row.label}-${i}`}
            className={`border-b last:border-0 ${row.strong ? "font-semibold bg-gray-50" : ""}`}
          >
            <td className={`px-3 py-1.5 ${row.indent ? "pl-6 text-gray-600" : ""}`}>
              {row.label}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums">{money(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
